import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * SaveSearchButton — captures the current filter state and creates a saved-search alert.
 * A "Name this alert" modal (dark + light mode) collects the alert name.
 */
export default function SaveSearchButton({ filters, compact = false, testid = "save-search-btn", sizeClass = "h-9 w-9" }) {
    const { user } = useAuth();
    const { theme } = useTheme();
    const isLight = theme === "light";
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");

    const openDialog = () => {
        if (!user) {
            navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
            return;
        }
        setName("");
        setOpen(true);
    };

    const save = async () => {
        if (!name.trim()) { toast.error("Alert name is required"); return; }
        setSaving(true);
        try {
            const alertDomains = (filters.domains || []).filter((d) => d !== "trainer");
            await api.post("/saved-searches", {
                label: name.trim(),
                domains: alertDomains,
                sports: filters.sports,
                states: filters.states,
                ages: filters.ages,
                grades: filters.grades || [],
                genders: filters.genders,
                circuits: filters.circuits,
                event_types: [],
                search: filters.search || "",
                near_zip: filters.near_zip || "",
                radius_miles: filters.radius_miles ? String(filters.radius_miles) : "",
                email_enabled: true,
            });
            toast.success("Alert saved — you'll be emailed when new matches are added.");
            setOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save alert");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <button
                type="button"
                data-testid={testid}
                onClick={openDialog}
                disabled={saving}
                title={user ? "Save search" : "Log in to save this search"}
                className={compact
                    ? `${sizeClass} inline-flex items-center justify-center border border-[#2a2a2a] hover:border-[#ff4500] hover:text-[#ff4500] text-[#a0a0a0] bg-[#141414] transition-colors disabled:opacity-60 shrink-0`
                    : "h-14 inline-flex items-center justify-center px-4 border border-[#2a2a2a] hover:border-[#ff4500] hover:text-[#ff4500] text-white bg-[#141414] transition-colors disabled:opacity-60"}
            >
                {saving ? <BellRing className="h-4 w-4 animate-pulse" /> : <Bell className="h-4 w-4" />}
            </button>

            {open && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="name-alert-modal" onMouseDown={() => setOpen(false)}>
                    <div className={`w-full max-w-md border ${isLight ? "bg-white border-gray-200 text-gray-900" : "bg-[#141414] border-[#2a2a2a] text-white"}`} onMouseDown={(e) => e.stopPropagation()}>
                        <div className={`flex items-center justify-between px-4 py-3 border-b ${isLight ? "border-gray-200" : "border-[#2a2a2a]"}`}>
                            <div className="text-xs uppercase tracking-[0.2em] text-[#ff4500] font-semibold">Name this alert</div>
                            <button type="button" data-testid="name-alert-close" onClick={() => setOpen(false)} className={isLight ? "text-gray-500 hover:text-gray-900" : "text-[#a0a0a0] hover:text-white"}><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-4 space-y-4">
                            <input
                                autoFocus
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                                placeholder="e.g. Basketball FL Teams"
                                data-testid="name-alert-input"
                                className={`w-full h-11 border outline-none text-sm px-3 focus:border-[#ff4500] ${isLight ? "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400" : "bg-[#0f0f0f] border-[#2a2a2a] text-white placeholder-[#666]"}`}
                            />
                            <div className="flex items-center gap-2 pt-1">
                                <button type="button" data-testid="name-alert-clear" onClick={() => setName("")} className={`text-xs uppercase tracking-wider px-2 py-2 ${isLight ? "text-gray-500 hover:text-gray-900" : "text-[#a0a0a0] hover:text-white"}`}>Clear</button>
                                <div className="flex-1" />
                                <button type="button" onClick={() => setOpen(false)} className={`text-xs uppercase tracking-wider px-4 py-2.5 border ${isLight ? "border-gray-300 text-gray-700 hover:border-gray-900" : "border-[#2a2a2a] hover:border-white"}`}>Cancel</button>
                                <button type="button" data-testid="name-alert-save" onClick={save} disabled={saving || !name.trim()} className="text-xs uppercase tracking-wider font-semibold px-4 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white disabled:opacity-50">
                                    {saving ? "Saving…" : "Save Alert"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
